import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import MainLoader from "../../components/loading/MainLoader";
import AlertModal from "../../components/alertModal/alertModal";
import Title from "../../components/title/Title";
import { BASE_URL } from "../../const/URL";
import { getCustomerIdentity } from "../../store/actions/fault.action";

interface MessageFormProps {
  onClose: () => void;
  fetchMessages?: () => void;
}

const MessageForm: React.FC<MessageFormProps> = ({
  onClose,
  fetchMessages,
}) => {
  const [message, setMessage] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [circuitId, setCircuitId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState({
    to: false,
    subject: false,
    message: false,
  });

  const API_URL_NEWMESSAGE = `${BASE_URL}/api/v1/create-conversation-message`;
  const API_URL_ATTACHMENT = `${BASE_URL}/api/v1/attachments-upload`;

  // Handle file attachment
  const handleAttachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    setFile(null);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [managerId, setManagerId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showErrorAlertV2, setShowErrorAlertV2] = useState(false);

  //Get Internal Managers
  const fetchManagers = useCallback(async () => {
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await axios.get(
        `${BASE_URL}/api/customer/internal-users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = response.data.dataBundle;

      if (data && data.length > 0) {
        setTo(`${data[0].name}`);
        setManagerId(data[0].id);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching managers:", error);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchManagers();
  }, [fetchManagers]);

  const handleSubmit = async () => {
    // Validate form fields (circuitId is now optional)
    const newErrors = {
      to: !to.trim(),
      subject: !subject.trim(),
      message: !message.trim(),
    };

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    try {
      if (circuitId.trim()) {
        try {
          await getCustomerIdentity(circuitId);
        } catch (identityError: any) {
          setShowErrorAlertV2(true);
          setErrorMessage(
            identityError?.message ||
              "Error verifying Service ID. Please try again or contact support."
          );
          return;
        }
      }

      const token = localStorage.getItem("enterprise_portal_access_Token");
      const CustomerID = localStorage.getItem("CustomerID");
      const userName = localStorage.getItem("UserName");

      if (!token || !CustomerID || !userName) {
        throw new Error("Missing authentication data");
      }

      // Step 1: Send the message
      const response = await axios.post(
        API_URL_NEWMESSAGE,
        {
          subject: subject,
          circuit: circuitId.trim() || null,
          origin: userName,
          companyId: Number(CustomerID),
          internalUsers: managerId,
          messageBody: message,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const messageID = response.data.dataBundle.messageId;

      // Step 2: If there's a file, upload it
      if (file) {
        const formData = new FormData();
        formData.append("file", file);

        await axios.post(`${API_URL_ATTACHMENT}/${messageID}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      // Step 3: Re-fetch messages if the callback is provided
      if (fetchMessages) {
        await fetchMessages();
      }

      setShowAlert(true);
    } catch (error) {
      // Check if this is an axios error with response data
      if (axios.isAxiosError(error) && error.response) {
        const apiError = error.response.data;
        if (apiError.displayMessage) {
          setShowErrorAlertV2(true);
          setErrorMessage(apiError.displayMessage);
        } else {
          setShowErrorAlert(true);
        }
      } else {
        setShowErrorAlert(true);
      }
      console.error("Error submitting message:", error);
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
    setShowErrorAlert(false);
    setShowErrorAlertV2(false);
    onClose();
  };

  return (
    <Box
      sx={{
        bgcolor: "#f8fbfe",
        padding: 3,
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Title>Compose Message</Title>
      <MainLoader show={isLoading} />
      <AlertModal
        show={showAlert}
        variant="success"
        buttons={[
          {
            text: "Ok",
            onClick: handleAlertClose,
            variant: "success",
          },
        ]}
      >
        <label>Your Message has been sent successfully.</label>
      </AlertModal>
      <AlertModal
        show={showErrorAlert}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: handleAlertClose,
            variant: "success",
          },
        ]}
      >
        <label>
          Your Message has not been sent successfully. Please check your details
          and try again.
        </label>
      </AlertModal>
      <AlertModal
        show={showErrorAlertV2}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: handleAlertClose,
            variant: "success",
          },
        ]}
      >
        <label>{errorMessage}</label>
      </AlertModal>

      {/* To Field */}
      <TextField
        fullWidth
        label="To"
        variant="standard"
        value={to}
        disabled
        onChange={(e) => setTo(e.target.value)}
        error={errors.to}
        helperText={errors.to ? "This field is required" : ""}
        sx={{ mb: 2 }}
      />

      {/* Circuit ID Field - Now Optional */}
      <TextField
        fullWidth
        label="Service ID (Optional)"
        variant="standard"
        value={circuitId}
        onChange={(e) => setCircuitId(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Subject Field */}
      <Select
        fullWidth
        displayEmpty
        variant="standard"
        value={subject}
        onChange={(e) => setSubject(e.target.value as string)}
        error={errors.subject}
        sx={{ mb: 2 }}
      >
        <MenuItem value="">Select Subject</MenuItem>
        <MenuItem value="Change IP address">Change IP address</MenuItem>
        <MenuItem value="Duplicate LAN IP">Duplicate LAN IP</MenuItem>
        <MenuItem value="Request for Quotation">Request for Quotation</MenuItem>
        <MenuItem value="Firewall rules configurations">
          Firewall rules configurations
        </MenuItem>
        <MenuItem value="Not contactable">Not contactable</MenuItem>
        <MenuItem value="Other">Other</MenuItem>
      </Select>
      {errors.subject && (
        <Typography variant="caption" color="error">
          This field is required
        </Typography>
      )}

      {/* Message Field */}
      <TextField
        fullWidth
        multiline
        minRows={4}
        variant="outlined"
        placeholder="Enter your message here..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        error={errors.message}
        helperText={errors.message ? "This field is required" : ""}
        sx={{ mb: 2, bgcolor: "white", borderRadius: 1 }}
      />

      {/* File Attachment */}
      {file && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={file.name}
            onDelete={handleRemoveFile}
            sx={{ bgcolor: "white" }}
          />
        </Box>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="contained"
          sx={{ bgcolor: "#0c3c78", color: "white" }}
          onClick={handleSubmit}
        >
          Send
        </Button>

        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <input
            accept="*"
            style={{ display: "none" }}
            id="icon-button-file"
            type="file"
            onChange={handleAttachFile}
          />
          <label htmlFor="icon-button-file">
            <IconButton component="span">
              <AttachFileIcon />
            </IconButton>
          </label>
          {/* <IconButton onClick={handleRemoveFile} disabled={!file}>
            <DeleteIcon />
          </IconButton> */}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageForm;
